import React, { useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import moment from "moment";
import axios from "axios";
import { Spin } from "antd";
import config from "../../../config";
import PrintHeaderAccount from "../../../Pdf/PrintHeaderAccount";
import PrintFooter from "../../../Pdf/PrintFooter";
import { saleReportStyles } from "../../../Pdf/pdfStyles";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../../Pdf/PrintFormateNumber";

const COL = {
  leftLabel: 170,
  leftAmount: 113,
  rightLabel: 170,
  rightAmount: 114,
};

const TABLE_WIDTH =
  COL.leftLabel + COL.leftAmount + COL.rightLabel + COL.rightAmount;

const ASSET_ROWS = [
  { label: "Stock value (at cost)", key: "productBalance" },
  { label: "Customer receivable", key: "customerReceiveable" },
  { label: "Supplier receivable", key: "supplierReceiveable" },
  { label: "Bank balance", key: "bankBalance" },
  { label: "Cash in hand", key: "cashBalance" },
];

const LIABILITY_ROWS = [
  { label: "Customer payable", key: "customerPayable" },
  { label: "Supplier payable", key: "supplierPayable" },
  { label: "Capital", key: "capitalBalance" },
];

const fmt = (val) => PrintFormateNumber(parseReportAmount(val), 0);

function buildSideBySideRows(data) {
  const leftRows = ASSET_ROWS.map((row) => ({
    label: row.label,
    amount: data[row.key],
  }));
  const rightRows = LIABILITY_ROWS.map((row) => ({
    label: row.label,
    amount: data[row.key],
  }));
  const count = Math.max(leftRows.length, rightRows.length);
  const rows = [];
  for (let i = 0; i < count; i++) {
    rows.push({
      left: leftRows[i],
      right: rightRows[i],
    });
  }
  return rows;
}

const PrintFinancialSummary = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const AsOfDate = urlParams.get("AsOfDate");

  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const [summaryDetail, setSummaryDetail] = useState({});
  const [loading, setLoading] = useState(false);

  const formattedAsOf = AsOfDate
    ? moment(AsOfDate).isValid()
      ? moment(AsOfDate).format("YYYY-MM-DD")
      : AsOfDate
    : moment().format("YYYY-MM-DD");

  useEffect(() => {
    document.title = "Print Financial Summary";
    if (!UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Report/GetFinancialSummary",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        EndDate: formattedAsOf,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setSummaryDetail(response.data);
        } else {
          setSummaryDetail({});
        }
        setLoading(false);
      })
      .catch(function () {
        setSummaryDetail({});
        setLoading(false);
      });
  }, [formattedAsOf, UserID, AccessKey]);

  const asOfLabel =
    summaryDetail.asOfDate && moment(summaryDetail.asOfDate).isValid()
      ? moment(summaryDetail.asOfDate).format(config.date_format)
      : moment(formattedAsOf).isValid()
      ? moment(formattedAsOf).format(config.date_format)
      : "—";

  const tableRows = useMemo(
    () => buildSideBySideRows(summaryDetail),
    [summaryDetail]
  );

  const totals = useMemo(() => {
    const totalAssets =
      parseReportAmount(summaryDetail.totalAssets) ||
      ASSET_ROWS.reduce(
        (sum, row) => sum + parseReportAmount(summaryDetail[row.key]),
        0
      );
    const totalLiabilities =
      parseReportAmount(summaryDetail.totalLiabilities) ||
      LIABILITY_ROWS.reduce(
        (sum, row) => sum + parseReportAmount(summaryDetail[row.key]),
        0
      );
    const grossAmount =
      parseReportAmount(summaryDetail.grossAmount) ||
      totalAssets - totalLiabilities;
    return { totalAssets, totalLiabilities, grossAmount };
  }, [summaryDetail]);

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>Financial Summary</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              As of: {asOfLabel}
            </Text>
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={[saleReportStyles.tableHead, { width: TABLE_WIDTH }]}>
              <View style={[saleReportStyles.th, { width: COL.leftLabel }]}>
                <Text style={saleReportStyles.thValue}>Assets / Receivables</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.leftAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.rightLabel }]}>
                <Text style={saleReportStyles.thValue}>
                  Liabilities / Payables
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Amount</Text>
              </View>
            </View>

            {tableRows.map((row, index) => (
              <View key={index} style={saleReportStyles.row}>
                <View style={[saleReportStyles.td, { width: COL.leftLabel }]}>
                  <Text style={saleReportStyles.tdValue}>
                    {row.left?.label || ""}
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.leftAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {row.left ? fmt(row.left.amount) : ""}
                  </Text>
                </View>
                <View style={[saleReportStyles.td, { width: COL.rightLabel }]}>
                  <Text style={saleReportStyles.tdValue}>
                    {row.right?.label || ""}
                  </Text>
                </View>
                <View
                  style={[
                    saleReportStyles.td,
                    { width: COL.rightAmount, textAlign: "right" },
                  ]}
                >
                  <Text style={saleReportStyles.tdValue}>
                    {row.right ? fmt(row.right.amount) : ""}
                  </Text>
                </View>
              </View>
            ))}

            <View style={saleReportStyles.totalsRow}>
              <View style={[saleReportStyles.th, { width: COL.leftLabel }]}>
                <Text style={saleReportStyles.thValue}>Total assets</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.leftAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.totalAssets)}
                </Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.rightLabel }]}>
                <Text style={saleReportStyles.thValue}>Total liabilities</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.totalLiabilities)}
                </Text>
              </View>
            </View>
          </View>

          <View
            style={[
              saleReportStyles.table,
              { width: TABLE_WIDTH, marginTop: 8 },
            ]}
          >
            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: COL.leftLabel + COL.leftAmount + COL.rightLabel,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  Net position (assets − liabilities)
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.rightAmount, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(totals.grossAmount)}
                </Text>
              </View>
            </View>
          </View>

          <View
            style={{ marginBottom: 20, marginTop: 0, top: 6, bottom: 6 }}
            fixed
          >
            <Text
              style={{ fontSize: "7pt", color: "#000" }}
              render={({ pageNumber, totalPages }) =>
                `Page ${pageNumber} / ${totalPages}`
              }
            />
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%" fileName="FinancialSummary.pdf">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
};

export default PrintFinancialSummary;
